import { useMemo } from "react";

interface TenantInfo {
  subdominio: string;
  isCustomDomain: boolean;
  isLocalhost: boolean;
  hostname: string;
  fullUrl: string;
}

/**
 * Mapeamento de domínios customizados para subdomínios
 * Adicione aqui novos domínios personalizados
 */
const CUSTOM_DOMAIN_MAP: Record<string, string> = {
  "desordibroker.com.br": "desordibroker",
  "www.desordibroker.com.br": "desordibroker",
  // Adicione mais domínios customizados aqui:
  // 'minhaimobiliaria.com.br': 'minhaimobiliaria',
};

/**
 * Subdomínio padrão para desenvolvimento e fallback
 */
const DEFAULT_SUBDOMAIN = "desordibroker";

/**
 * Hook para detectar e gerenciar informações do tenant (imobiliária)
 * baseado no subdomínio ou domínio customizado
 */
export function useTenant(): TenantInfo {
  return useMemo(() => {
    const hostname = window.location.hostname;
    const fullUrl = window.location.href;

    // 1. Localhost - usar subdomínio padrão
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return {
        subdominio: DEFAULT_SUBDOMAIN,
        isCustomDomain: false,
        isLocalhost: true,
        hostname,
        fullUrl,
      };
    }

    // 2. Ambiente Lovable preview - usar subdomínio padrão
    if (hostname.endsWith(".lovableproject.com")) {
      return {
        subdominio: DEFAULT_SUBDOMAIN,
        isCustomDomain: false,
        isLocalhost: false,
        hostname,
        fullUrl,
      };
    }

    if (hostname.endsWith(".housebit.com.br")) {
      const subdominio = hostname.split(".housebit.com.br")[0];
      return {
        subdominio,
        isCustomDomain: false,
        isLocalhost: false,
        hostname,
        fullUrl,
      };
    }

    // 3. Domínio customizado - verificar no mapeamento
    if (CUSTOM_DOMAIN_MAP[hostname]) {
      return {
        subdominio: CUSTOM_DOMAIN_MAP[hostname],
        isCustomDomain: true,
        isLocalhost: false,
        hostname,
        fullUrl,
      };
    }

    // 3. Subdomínio Lovable (ex: desordibroker.lovable.app)
    const parts = hostname.split(".");

    // Se tem pelo menos 3 partes, o primeiro é o subdomínio
    // Exemplo: desordibroker.lovable.app -> ["desordibroker", "lovable", "app"]
    if (parts.length >= 3) {
      const subdominio = parts[0];
      return {
        subdominio,
        isCustomDomain: false,
        isLocalhost: false,
        hostname,
        fullUrl,
      };
    }

    // 4. Fallback - usar subdomínio padrão
    console.warn(`Não foi possível detectar subdomínio de "${hostname}". Usando padrão: ${DEFAULT_SUBDOMAIN}`);
    return {
      subdominio: DEFAULT_SUBDOMAIN,
      isCustomDomain: false,
      isLocalhost: false,
      hostname,
      fullUrl,
    };
  }, []);
}

/**
 * Hook para verificar se o tenant atual é válido
 */
export function useTenantValidation() {
  const tenant = useTenant();

  // Aqui você pode adicionar validação adicional
  // Por exemplo, verificar se o tenant existe no banco
  const isValid = tenant.subdominio !== "";

  return {
    ...tenant,
    isValid,
  };
}

/**
 * Obtém informações do tenant sem ser um hook (útil em funções utilitárias)
 */
export function getTenantInfo(): TenantInfo {
  const hostname = window.location.hostname;
  const fullUrl = window.location.href;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return {
      subdominio: DEFAULT_SUBDOMAIN,
      isCustomDomain: false,
      isLocalhost: true,
      hostname,
      fullUrl,
    };
  }

  // Ambiente Lovable preview
  if (hostname.endsWith(".lovableproject.com")) {
    return {
      subdominio: DEFAULT_SUBDOMAIN,
      isCustomDomain: false,
      isLocalhost: false,
      hostname,
      fullUrl,
    };
  }

  if (hostname.endsWith(".housebit.com.br")) {
    const subdominio = hostname.split(".housebit.com.br")[0];
    return {
      subdominio,
      isCustomDomain: false,
      isLocalhost: false,
      hostname,
      fullUrl,
    };
  }

  if (CUSTOM_DOMAIN_MAP[hostname]) {
    return {
      subdominio: CUSTOM_DOMAIN_MAP[hostname],
      isCustomDomain: true,
      isLocalhost: false,
      hostname,
      fullUrl,
    };
  }

  const parts = hostname.split(".");
  if (parts.length >= 3) {
    return {
      subdominio: parts[0],
      isCustomDomain: false,
      isLocalhost: false,
      hostname,
      fullUrl,
    };
  }

  return {
    subdominio: DEFAULT_SUBDOMAIN,
    isCustomDomain: false,
    isLocalhost: false,
    hostname,
    fullUrl,
  };
}

/**
 * Adiciona um domínio customizado ao mapeamento (runtime)
 */
export function addCustomDomain(domain: string, subdominio: string) {
  CUSTOM_DOMAIN_MAP[domain] = subdominio;
  console.log(`Domínio customizado adicionado: ${domain} -> ${subdominio}`);
}

/**
 * Remove um domínio customizado do mapeamento
 */
export function removeCustomDomain(domain: string) {
  delete CUSTOM_DOMAIN_MAP[domain];
  console.log(`Domínio customizado removido: ${domain}`);
}

/**
 * Lista todos os domínios customizados configurados
 */
export function listCustomDomains(): Record<string, string> {
  return { ...CUSTOM_DOMAIN_MAP };
}
